'use client';

import { useRef, useState, type ReactNode, type TouchEvent } from 'react';

/**
 * 通用下拉刷新（v0.8 体验强化包）：
 * 移动端（触屏）在页面顶部下拉触发 onRefresh；带进度指示器。
 * 仅触屏生效（CSS media 判断），桌面端无感。
 */

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: ReactNode;
  /** 触发阈值（px），默认 64 */
  threshold?: number;
  /** 刷新中/提示文案（用于指示器） */
  refreshingLabel?: string;
  pullLabel?: string;
}

export default function PullToRefresh({
  onRefresh,
  children,
  threshold = 64,
  refreshingLabel = '刷新中…',
  pullLabel = '下拉刷新',
}: PullToRefreshProps) {
  const [progress, setProgress] = useState(0); // 0..1
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const activeRef = useRef(false);
  const progressRef = useRef(0);

  const onTouchStart = (e: TouchEvent) => {
    if (refreshing) return;
    // 仅当页面滚动到顶部时才允许下拉
    if (window.scrollY > 0) return;
    // 排除从可滚动子容器内部开始的手势（避免与横向/内部滚动冲突）
    startY.current = e.touches[0].clientY;
    activeRef.current = true;
  };

  const onTouchMove = (e: TouchEvent) => {
    if (!activeRef.current || startY.current == null || refreshing) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy <= 0) {
      progressRef.current = 0;
      setProgress(0);
      return;
    }
    // 阻尼：越拉越费力，最多到 1.2 再回弹
    const p = Math.min(1.2, dy / threshold);
    progressRef.current = p;
    setProgress(Math.min(1, p));
  };

  const onTouchEnd = () => {
    if (!activeRef.current || startY.current == null) return;
    activeRef.current = false;
    startY.current = null;
    if (progressRef.current >= 1 && !refreshing) {
      void (async () => {
        setRefreshing(true);
        try {
          await onRefresh();
        } finally {
          setRefreshing(false);
          progressRef.current = 0;
          setProgress(0);
        }
      })();
    } else {
      progressRef.current = 0;
      setProgress(0);
    }
  };

  const indicatorH = 44;
  const pullOffset = Math.min(1, progress) * indicatorH;

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      className="relative touch-pan-y"
    >
      {/* 指示器（absolute 悬浮，不占布局） */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center overflow-hidden transition-transform duration-150"
        style={{ height: refreshing ? indicatorH : pullOffset }}
      >
        <div className="mt-2 flex h-7 items-center gap-2 rounded-full border border-gf-border bg-gf-surface2/95 px-3 shadow-lg backdrop-blur">
          {refreshing ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gf-accent border-t-transparent" />
          ) : (
            <svg
              viewBox="0 0 16 16"
              className={`h-3.5 w-3.5 text-gf-accent transition-transform duration-150 ${progress >= 1 ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M8 12V4M4 8l4-4 4 4" />
            </svg>
          )}
          <span className="text-xs font-medium text-gf-muted">
            {refreshing ? refreshingLabel : progress >= 1 ? '松手刷新' : pullLabel}
          </span>
        </div>
      </div>
      {/* 内容随下拉轻微下移（仅触屏时有 progress） */}
      <div style={{ transform: refreshing ? `translateY(${indicatorH * 0.6}px)` : `translateY(${pullOffset * 0.6}px)` }}>
        {children}
      </div>
    </div>
  );
}
